import React, { useState, useEffect } from "react";
import { findTemplatesByPositionId } from "../../services/template.service.ts";
import { notification } from "../../utils/Notifications.tsx";

interface Document {
  id: number;
  name: string;
  obligatory: boolean;
  fields: any[];
}

interface Template {
  template: {
    id: number;
    inmediate: boolean;
  };
  documents: Document[];
}

interface PositionDocumentsSelectorProps {
  positionId: number | null;
  onDocumentsChange: (selectedDocuments: number[]) => void;
  selectedDocuments?: number[];
  hideIfEmpty?: boolean;
}

const PositionDocumentsSelector: React.FC<PositionDocumentsSelectorProps> = ({
  positionId,
  onDocumentsChange,
  selectedDocuments = [],
  hideIfEmpty = false,
}) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchContainerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (positionId) {
      fetchDocuments();
    } else {
      setTemplates([]);
      setAllDocuments([]);
      onDocumentsChange([]);
    }
  }, [positionId]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const el = searchContainerRef.current as any;
      if (
        el &&
        !el.contains(event.target) &&
        !event.target.closest(".document-dropdown-item") &&
        !event.target.closest('input[type="checkbox"]') &&
        !event.target.closest("button")
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  const fetchDocuments = async () => {
    if (!positionId) return;

    setLoading(true);
    try {
      const response = await findTemplatesByPositionId(positionId);
      const templatesData = response.templates || [];
      setTemplates(templatesData);

      const documentsList: Document[] = [];

      // Documentos legacy y especiales que deben ocultarse
      const documentsToHide = [
        "Foto colaborador",
        "INE frente y vuelta",
        "Comprobante de domicilio actual",
        "Original solicitud de empleo",
        "Pruebas psicométricas aplicadas por reclutamiento",
        "Notas del reclutador",
      ];

      templatesData.forEach((template: Template) => {
        template.documents.forEach((doc: Document) => {
          // Ocultar documentos legacy y notas del reclutador
          if (documentsToHide.includes(doc.name)) {
            return;
          }
          if (!documentsList.find((d) => d.id === doc.id)) {
            documentsList.push(doc);
          }
        });
      });
      setAllDocuments(documentsList);
    } catch (error) {
      console.error("Error fetching documents:", error);
      notification("Error", "No se pudieron cargar los documentos", "danger");
      setTemplates([]);
      setAllDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentToggle = (documentId: number) => {
    const isSelected = selectedDocuments.includes(documentId);
    let newSelected: number[];

    if (isSelected) {
      newSelected = selectedDocuments.filter((id) => id !== documentId);
    } else {
      newSelected = [...selectedDocuments, documentId];
    }

    onDocumentsChange(newSelected);
  };

  const handleSelectAll = () => {
    const allSelected = allDocuments.every((doc) =>
      selectedDocuments.includes(doc.id)
    );

    if (allSelected) {
      onDocumentsChange([]);
    } else {
      const allIds = allDocuments.map((doc) => doc.id);
      onDocumentsChange(allIds);
    }
  };

  const filteredDocuments = allDocuments.filter((doc) =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedDocumentsList = allDocuments.filter((doc) =>
    selectedDocuments.includes(doc.id)
  );

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <span className="ms-2">Cargando documentos...</span>
      </div>
    );
  }

  if (!positionId) {
    return (
      <div className="alert alert-warning mb-3">
        <i className="fas fa-exclamation-triangle me-2"></i>
        <strong>Selecciona un puesto para ver los documentos requeridos</strong>
      </div>
    );
  }

  if (allDocuments.length === 0) {
    if (hideIfEmpty) return null;
    return (
      <div className="alert alert-info mb-3">
        <i className="fas fa-info-circle me-2"></i>
        <strong>Este puesto no tiene documentos asignados</strong>
      </div>
    );
  }

  return (
    <div className="card border-0 bg-light">
      <div className="card-body p-3">
        <h6 className="card-title mb-3 text-dark">
          <i className="fas fa-file-alt me-2"></i>
          Documentos Requeridos del Puesto
        </h6>

        {/* Búsqueda y selector */}
        <div className="mb-3">
          <label className="form-label">Seleccionar Documentos</label>
          <div className="position-relative" ref={searchContainerRef}>
            <div className="input-group">
              <div
                className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                style={{ zIndex: 10 }}
              >
                <i className="fa fa-search opacity-5" />
              </div>
              <input
                type="text"
                className="form-control px-35px bg-light"
                placeholder="Buscar documentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {
                  setShowDropdown(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowDropdown(false);
                }}
              />
            </div>

            {showDropdown && (
              <div
                className="position-absolute w-100 bg-white border rounded shadow-sm"
                style={{
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                  marginTop: "2px",
                }}
              >
                {/* Documentos seleccionados primero */}
                {selectedDocumentsList.length > 0 && (
                  <>
                    <div className="px-3 py-2 border-bottom bg-light">
                      <small className="text-muted fw-bold">
                        <i className="fa fa-check-circle me-1 text-success"></i>
                        Seleccionados ({selectedDocumentsList.length})
                      </small>
                    </div>
                    {selectedDocumentsList.map((doc) => (
                      <div
                        key={doc.id}
                        className="px-3 py-2 cursor-pointer d-flex align-items-center document-dropdown-item"
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#e3f2fd",
                        }}
                        onClick={() => handleDocumentToggle(doc.id)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#d1e7dd")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e3f2fd")
                        }
                      >
                        <input
                          type="checkbox"
                          checked={true}
                          onChange={() => {}}
                          className="me-2"
                          style={{ width: 16, height: 16 }}
                        />
                        <i className="fa fa-file me-2 text-success"></i>
                        <span className="flex-grow-1">{doc.name}</span>
                        {doc.obligatory && (
                          <span className="badge bg-danger ms-2">
                            Obligatorio
                          </span>
                        )}
                      </div>
                    ))}
                  </>
                )}

                {/* Botón seleccionar todos */}
                <div
                  className="px-3 py-2 border-top border-bottom bg-light document-dropdown-item"
                  onClick={handleSelectAll}
                  style={{ cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={allDocuments.every((doc) =>
                      selectedDocuments.includes(doc.id)
                    )}
                    onChange={() => {}}
                    className="me-2"
                    style={{ width: 16, height: 16 }}
                  />
                  <strong className="text-primary">
                    <i className="fa fa-check-double me-1"></i>
                    {allDocuments.every((doc) =>
                      selectedDocuments.includes(doc.id)
                    )
                      ? "Deseleccionar todos"
                      : "Seleccionar todos"}
                  </strong>
                </div>

                {/* Documentos disponibles */}
                {filteredDocuments
                  .filter((doc) => !selectedDocuments.includes(doc.id))
                  .map((doc) => (
                    <div
                      key={doc.id}
                      className="px-3 py-2 cursor-pointer d-flex align-items-center document-dropdown-item"
                      style={{
                        cursor: "pointer",
                        backgroundColor: "white",
                      }}
                      onClick={() => handleDocumentToggle(doc.id)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f8f9fa")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = "white")
                      }
                    >
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => {}}
                        className="me-2"
                        style={{ width: 16, height: 16 }}
                      />
                      <i className="fa fa-file me-2 text-info"></i>
                      <span className="flex-grow-1">{doc.name}</span>
                      {doc.obligatory && (
                        <span className="badge bg-danger ms-2">
                          Obligatorio
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Resumen de selección */}
        {selectedDocuments.length > 0 && (
          <div className="alert alert-info mb-0">
            <i className="fa fa-info-circle me-2"></i>
            {selectedDocuments.length} documento
            {selectedDocuments.length > 1 ? "s" : ""} seleccionado
            {selectedDocuments.length > 1 ? "s" : ""}
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionDocumentsSelector;
